import type { CollabActivitySubmissionDto } from '@/api/generated/types.gen';
import type { PickedPhoto } from '@/components/mobile/PhotoPicker';
import { pentruServer } from '@/domains/lessons/utils/clase-canonice';
import type { TipColab } from '../utils/tipuri-colab';

export type Step = 'choose' | 'activity' | 'details' | 'photos' | 'done';

export interface SubmissionState {
    step: Step;
    lessonId: string | null;
    /** Tipul activitatii alese: decide ce cere formularul (clase si elevi doar pe activitatea practica). */
    activityType: TipColab | null;
    submittedByName: string;
    description: string;
    /** Textul brut al claselor, cum l-a scris omul (vezi CampClase). */
    clase: string;
    /** Text, nu numar: campul poate fi gol sau la jumatate de tastare. */
    numarElevi: string;
    /** Fragmentele de clase pe care serverul nu le-a inteles (400). */
    fragmenteRespinse: string[];
    photos: PickedPhoto[];
    lessonAnswerId: string | null;
}

export type Action =
    | { type: 'chooseActivity'; lessonId: string; activityType?: TipColab | null }
    | { type: 'setDetails'; submittedByName: string; description: string; clase?: string; numarElevi?: string }
    | { type: 'setPhotos'; photos: PickedPhoto[] }
    | { type: 'next' }
    | { type: 'back' }
    | { type: 'claseRespinse'; fragmente: string[] }
    | { type: 'submitted'; lessonAnswerId: string }
    | { type: 'reset' };

export const initialState: SubmissionState = {
    step: 'choose', lessonId: null, activityType: null, submittedByName: '', description: '',
    clase: '', numarElevi: '', fragmenteRespinse: [], photos: [], lessonAnswerId: null,
};

const ORDER: Step[] = ['choose', 'activity', 'details', 'photos', 'done'];

/** Clasele se cer doar pe activitatea practica; la exercitiu serverul le ignora. */
export function cereClase(state: Pick<SubmissionState, 'activityType'>): boolean {
    return state.activityType === 'PRACTICAL_ACTIVITY';
}

/**
 * La exercitiul cu clasa numarul de elevi e OBLIGATORIU, minim 2 — ca pe pagina profesorului
 * (`CampParticipanti`). Sesizarea ECOTIC din 2026-10-26: prin link nu se cerea deloc, iar toate
 * raspunsurile ajungeau la admin cu „1 participant".
 */
export function cereParticipanti(state: Pick<SubmissionState, 'activityType'>): boolean {
    return state.activityType === 'CLASS_EXERCISE';
}

export const MINIM_PARTICIPANTI = 2;

/** Gol (optional) sau un intreg de la 1 in sus. */
export function numarEleviValid(text: string): boolean {
    const t = text.trim();
    return t === '' || (/^\d{1,6}$/.test(t) && Number(t) >= 1);
}

/** Obligatoriu: un intreg de la MINIM_PARTICIPANTI in sus. */
export function numarParticipantiValid(text: string): boolean {
    const t = text.trim();
    return /^\d{1,6}$/.test(t) && Number(t) >= MINIM_PARTICIPANTI;
}

/** Pura: un scop per ecran (UX 4.3), starea nu sare peste pasi. */
export function reducer(state: SubmissionState, action: Action): SubmissionState {
    switch (action.type) {
        case 'chooseActivity':
            // Alegerea duce la detaliile activitatii (descriere, film, fise), nu direct la formular.
            return { ...state, lessonId: action.lessonId, activityType: action.activityType ?? null, step: 'activity' };
        case 'setDetails':
            return {
                ...state,
                submittedByName: action.submittedByName,
                description: action.description,
                clase: action.clase ?? state.clase,
                numarElevi: action.numarElevi ?? state.numarElevi,
                // Omul a rescris ceva: eroarea serverului nu mai descrie ce e in camp.
                fragmenteRespinse: action.clase !== undefined && action.clase !== state.clase ? [] : state.fragmenteRespinse,
            };
        case 'setPhotos':
            return { ...state, photos: action.photos };
        case 'next': {
            const i = ORDER.indexOf(state.step);
            // ORDER.length - 2 exclude 'done': acolo se ajunge doar prin actiunea 'submitted',
            // dupa ce cererea a fost trimisa cu succes, nu prin simpla navigare inainte.
            return canContinue(state) && i < ORDER.length - 2 ? { ...state, step: ORDER[i + 1] } : state;
        }
        case 'back': {
            // 'done' e stare terminala: dupa o depunere reusita nu exista intoarcere la 'photos',
            // altfel utilizatorul ar ajunge pe ecranul de trimitere cu lessonAnswerId deja emis,
            // fara nicio iesire ('next' oricum nu-l mai poate readuce pe 'done').
            if (state.step === 'done') return state;
            const i = ORDER.indexOf(state.step);
            return i <= 0 ? state : { ...state, step: ORDER[i - 1] };
        }
        case 'claseRespinse':
            // Serverul n-a inteles clasele: omul e dus inapoi la campul lor, cu pozele pastrate.
            return { ...state, step: 'details', fragmenteRespinse: action.fragmente };
        case 'submitted':
            return { ...state, step: 'done', lessonAnswerId: action.lessonAnswerId };
        case 'reset':
            // Numele ramane: acelasi colaborator trimite de obicei mai multe activitati la rand.
            return { ...initialState, submittedByName: state.submittedByName };
    }
}

export function canContinue(state: SubmissionState): boolean {
    switch (state.step) {
        case 'choose': return state.lessonId !== null;
        case 'activity': return state.lessonId !== null;
        case 'details':
            return state.submittedByName.trim().length > 0
                && (!cereClase(state) || state.clase.trim().length > 0)
                && (!cereClase(state) || numarEleviValid(state.numarElevi))
                && (!cereParticipanti(state) || numarParticipantiValid(state.numarElevi));
        case 'photos': {
            // 'error' e un stadiu final ca 'done', doar ca nu produce fileId: o poza esuata nu
            // tine ecranul blocat cat timp exista cel putin o poza reusita si nimic nu mai e
            // efectiv in lucru (userul o poate sterge din PhotoPicker, dar nu e obligat).
            const done = state.photos.filter((p) => p.status === 'done');
            const busy = state.photos.some((p) => p.status === 'uploading' || p.status === 'pending');
            return done.length > 0 && !busy;
        }
        case 'done': return false;
    }
}

/**
 * Numarul de poze pe fiecare stadiu, pentru ecranul de confirmare din Task 14: userul poate apasa
 * "Trimite" cu o poza esuata inca in lista (nu blocheaza `canContinue`, C4) si trebuie sa afle
 * explicit ca ea nu pleaca odata cu cererea, nu doar sa vada tacut mai putine poze decat a ales.
 */
export function photoStats(state: SubmissionState): { done: number; failed: number; busy: number } {
    return {
        done: state.photos.filter((p) => p.status === 'done').length,
        failed: state.photos.filter((p) => p.status === 'error').length,
        busy: state.photos.filter((p) => p.status === 'uploading' || p.status === 'pending').length,
    };
}

export function toSubmissionBody(state: SubmissionState): CollabActivitySubmissionDto {
    if (!state.lessonId) throw new Error('Activitatea nu e aleasa.');
    const photoUrls = state.photos.filter((p) => p.status === 'done' && p.fileId).map((p) => p.fileId as string);
    if (photoUrls.length === 0) throw new Error('Nicio fotografie incarcata.');
    const elevi = state.numarElevi.trim();
    return {
        lessonId: state.lessonId,
        submittedByName: state.submittedByName.trim(),
        description: state.description.trim(),
        photoUrls,
        // Clasele doar pe activitatea practica; numarul de elevi pe ambele (obligatoriu la exercitiu).
        ...(cereClase(state) && state.clase.trim() ? { clase: pentruServer(state.clase) } : {}),
        ...(cereClase(state) && elevi && numarEleviValid(elevi) ? { numarElevi: Number(elevi) } : {}),
        ...(cereParticipanti(state) && numarParticipantiValid(elevi) ? { numarElevi: Number(elevi) } : {}),
    };
}
